// /src/pages/TokenSite.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronRight, Globe, Shield, Zap, Binary, Menu, X } from 'lucide-react';
import styles from './TokenSite.module.css';

const API_URL = process.env.REACT_APP_API_URL;

const formatTokenName = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-');
};

const TokenSite = ({ tokenName: tokenNameProp }) => {
    const { tokenName: tokenNameParam } = useParams();
    const navigate = useNavigate();
    const [siteData, setSiteData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Use prop if provided (subdomain case), otherwise use param (direct path case)
    const tokenName = tokenNameProp || tokenNameParam;

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.classList.add('menu-open');
        } else {
            document.body.classList.remove('menu-open');
        }
        return () => {
            document.body.classList.remove('menu-open');
        };
    }, [mobileMenuOpen]);

    // Theme effect
    useEffect(() => {
        if (siteData?.theme) {
            const tokenSiteElement = document.querySelector(`.${styles.tokenSite}`);
            if (tokenSiteElement) {
                tokenSiteElement.style.setProperty('--color-primary', siteData.theme.colors.primary);
                tokenSiteElement.style.setProperty('--color-secondary', siteData.theme.colors.secondary);
                tokenSiteElement.style.setProperty('--color-accent', siteData.theme.colors.accent);
            }
        }
    }, [siteData]);

    useEffect(() => {
        const fetchSiteData = async () => {
            if (!tokenName) {
                setError('No token name provided');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                console.log('Fetching data for token:', tokenName);
                const formattedName = formatTokenName(tokenName);
                const response = await fetch(`${API_URL}/api/config/${formattedName}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch website data: ${response.statusText}`);
                }

                const data = await response.json();
                if (data.status === 'error') {
                    throw new Error(data.message || 'Failed to load website');
                }

                console.log('Received site data:', data);
                setSiteData(data.data);
            } catch (err) {
                console.error('Error fetching site data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchSiteData();
    }, [tokenName]);

    if (loading) {
        return (
            <div className={styles.tokenSite}>
                <div className="flex flex-col items-center justify-center min-h-screen">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-white">Loading {tokenName} website...</p>
                </div>
            </div>
        );
    }

    if (error || !siteData) {
        return (
            <div className={styles.tokenSite}>
                <div className="flex flex-col items-center justify-center min-h-screen text-white p-4">
                    <div className="max-w-md text-center">
                        <h2 className="text-2xl font-bold text-red-500 mb-4">Website Not Found</h2>
                        <p className="text-gray-300 mb-6">{error || 'Unable to load website configuration'}</p>
                        <button
                            onClick={() => navigate('/')}
                            className={`${styles.gradientButton} px-6 py-2 rounded-lg font-bold hover:scale-105 transition-transform`}
                        >
                            Go Home
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.tokenSite}>
            {/* Navigation */}
            {/* Navigation */}
            <nav className={`${styles.navbarBg} relative`}>
                <div className="absolute inset-x-0 bottom-0 h-[1px] bg-gradient-to-r from-transparent via-blue-400/20 to-transparent"></div>
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        {/* Logo Section - Always Visible */}
                        <div className="flex items-center gap-3">
                            <div className={styles.gradientBorder + " p-[2px] rounded-full"}>
                                <img
                                    src={siteData.media.logo}
                                    alt="Logo"
                                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                                />
                            </div>
                            <span className={`text-lg sm:text-xl font-bold ${styles.gradientText}`}>
                                {siteData.token_info.name}
                            </span>
                        </div>

                        {/* Desktop Menu - Hidden on Mobile */}
                        <div className="hidden md:flex items-center gap-4">

                            <a href="https://kyora.ai"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group block px-3 py-1 border border-blue-400/20 rounded-xl bg-blue-400/5 backdrop-blur-sm hover:border-blue-400/30 hover:bg-blue-400/10 transition-all duration-300"
                            >
                                <div className="flex flex-col items-center gap-0.5">
                                    <span className="text-xs font-medium text-blue-400/80 group-hover:text-blue-400/90 transition-colors">Created by</span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-blue-300 to-blue-400">
                                            KYORA AI
                                        </span>
                                        <div className="relative">
                                            <div className="absolute -inset-[1px] bg-gradient-to-r from-blue-400/20 to-transparent rounded-full blur-[1px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                            <div className="relative w-5 h-5 rounded-full overflow-hidden border border-blue-400/30">
                                                <img
                                                    src="/kyora.png"
                                                    alt="Kyora AI"
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </a>

                            <a href={siteData.pumpfun_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`${styles.gradientButton} px-6 py-2.5 rounded-xl font-bold hover:scale-105 transition-transform flex items-center text-base whitespace-nowrap`}
                            >
                                Buy ${siteData.token_info.ticker}
                            </a>
                        </div>

                        {/* Mobile Menu Button */}
                        <div className="md:hidden">
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="p-2 text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                <Menu className="w-6 h-6" />
                            </button>
                        </div>
                    </div>
                </div >

                {/* Mobile Menu - Moved outside container */}
                <div
                    className="fixed inset-0 bg-white transition-transform duration-300 ease-in-out md:hidden"
                    style={{
                        transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
                        zIndex: 100
                    }}
                >
                    <div className="flex flex-col h-full bg-white">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <span className="text-lg font-bold text-gray-900">Menu</span>
                            <button
                                onClick={() => setMobileMenuOpen(false)}
                                className="p-2 text-gray-600 hover:text-gray-900"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 flex flex-col items-center justify-center gap-6 p-4 bg-white">
                            {/* Buy Token Button */}
                            <a
                                href={siteData.pumpfun_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`${styles.gradientButton} px-6 py-2.5 rounded-xl font-bold hover:scale-105 transition-transform flex items-center justify-center w-full max-w-xs text-base`}
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Buy ${siteData.token_info.ticker}
                            </a>

                            {/* Social Links */}
                            <div className="flex flex-col items-center gap-4 w-full max-w-xs">
                                <a
                                    href="https://x.com/ai_kyora"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors text-gray-700"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        className="text-gray-700"
                                    >
                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                    </svg>
                                    <span className="text-sm font-medium">Twitter</span>
                                </a>

                                <a
                                    href="https://discord.gg/KdT6mkmt"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors text-gray-700"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        className="text-gray-700"
                                    >
                                        <path d="M14.82 4.26a10.14 10.14 0 0 0-.53 1.1 14.66 14.66 0 0 0-4.58 0 10.14 10.14 0 0 0-.53-1.1 16 16 0 0 0-4.13 1.3 17.33 17.33 0 0 0-3 11.59 16.6 16.6 0 0 0 5.07 2.59A12.89 12.89 0 0 0 8.23 18a9.65 9.65 0 0 1-1.71-.83 3.39 3.39 0 0 0 .42-.33 11.66 11.66 0 0 0 10.12 0q.21.18.42.33a10.84 10.84 0 0 1-1.71.84 12.41 12.41 0 0 0 1.08 1.78 16.44 16.44 0 0 0 5.06-2.59 17.22 17.22 0 0 0-3-11.59 16.09 16.09 0 0 0-4.09-1.35zM8.68 14.81a1.94 1.94 0 0 1-1.8-2 1.93 1.93 0 0 1 1.8-2 1.93 1.93 0 0 1 1.8 2 1.93 1.93 0 0 1-1.8 2zm6.64 0a1.94 1.94 0 0 1-1.8-2 1.93 1.93 0 0 1 1.8-2 1.92 1.92 0 0 1 1.8 2 1.92 1.92 0 0 1-1.8 2z" />
                                    </svg>
                                    <span className="text-sm font-medium">Discord</span>
                                </a>
                            </div>

                            {/* Created by Kyora AI */}
                            <a
                                href="https://kyora.ai"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group block px-3 py-1 border border-blue-400/20 rounded-xl bg-blue-400/5 backdrop-blur-sm hover:border-blue-400/30 hover:bg-blue-400/10 transition-all duration-300"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                <div className="flex flex-col items-center gap-0.5">
                                    <span className="text-xs font-medium text-blue-400/80 group-hover:text-blue-400/90 transition-colors">
                                        Created by
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-blue-300 to-blue-400">
                                            KYORA AI
                                        </span>
                                        <div className="relative">
                                            <div className="absolute -inset-[1px] bg-gradient-to-r from-blue-400/20 to-transparent rounded-full blur-[1px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                            <div className="relative w-5 h-5 rounded-full overflow-hidden border border-blue-400/30">
                                                <img
                                                    src="/kyora.png"
                                                    alt="Kyora AI"
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </nav >
            {/* Banner Section */}
            < div className={styles.bannerSection} >
                <img
                    src={siteData.media.banner}
                    alt="Banner"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Remove transparent overlay, add more sophisticated gradient */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/80"></div>

                <div className="relative z-10 container mx-auto px-4 py-32">
                    <div className="max-w-4xl mx-auto text-center">
                        <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 [text-shadow:_0_2px_10px_rgb(0_0_0_/_20%)]">
                            {siteData.content.hero_section.title}
                        </h1>
                        <p className="text-xl md:text-2xl text-white/90 mb-8">
                            {siteData.content.hero_section.subtitle}
                        </p>
                        <a
                            href={siteData.pumpfun_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${styles.gradientButton} px-8 py-4 rounded-xl font-bold text-lg hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl`}
                        >
                            {siteData.content.hero_section.cta}
                        </a>
                    </div>
                </div>
            </div >

            {/* About Section */}
            < section className={styles.sectionAlt + " py-20"} >
                <div className="container mx-auto px-4">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className={`text-4xl font-bold mb-6 ${styles.gradientText}`}>
                            {siteData.content.about_section.title}
                        </h2>
                        <p className="text-xl text-gray-500 mb-12">
                            {siteData.content.about_section.content}
                        </p>

                        {/* Avatar Section */}
                        <div className="flex justify-center mb-8">
                            <div className="relative w-24 h-24">
                                {/* Border ring with gradient */}
                                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-primary to-transparent animate-spin-slow"></div>
                                {/* Avatar Image with nav-style border */}
                                <div className={styles.gradientBorder + " p-[2px] rounded-full"}>
                                    <img
                                        src={siteData.media.logo}
                                        alt="Avatar"
                                        className="w-full h-full rounded-full object-cover"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-12 px-4 py-8 md:px-8 lg:px-12">
                            {siteData.content.about_section.key_points.map((point, index) => (
                                <div key={index} className={styles.featureCard + " relative p-10 rounded-2xl overflow-hidden group cursor-pointer"}>
                                    {/* Border accent */}
                                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-30"></div>

                                    {/* Content */}
                                    <div className="relative z-10">
                                        <span className="inline-block px-3 py-1 rounded-full text-xs mb-4 bg-opacity-10"
                                            style={{
                                                backgroundColor: 'var(--color-primary)',
                                                color: 'var(--color-primary)',
                                                opacity: '0.1'
                                            }}>
                                            0{index + 1}
                                        </span>
                                        <h3 className="text-base font-bold leading-relaxed" style={{ color: 'var(--color-primary)' }}>
                                            {point}
                                        </h3>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section >

            {/* Features Section */}
            < section className="py-20" >
                <div className="container mx-auto px-4">
                    <h2 className={`text-4xl font-bold text-center mb-16 ${styles.gradientText}`}>Features</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {siteData.content.features.map((feature, index) => (
                            <div key={index} className={styles.featureCard + " p-8 rounded-2xl"}>
                                <div className="w-12 h-12 mb-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(var(--color-accent), 0.1)' }}>
                                    {index === 0 && <Zap className="w-6 h-6" style={{ color: 'var(--color-accent)' }} />}
                                    {index === 1 && <Shield className="w-6 h-6" style={{ color: 'var(--color-accent)' }} />}
                                    {index === 2 && <Globe className="w-6 h-6" style={{ color: 'var(--color-accent)' }} />}
                                </div>
                                <h3 className={`text-xl font-bold mb-4 ${styles.gradientText}`}>
                                    {feature.title}
                                </h3>
                                <p className="text-gray-800">
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section >

            {/* Tokenomics Section */}
            < section className={styles.sectionAlt + " py-20"} >
                <div className="container mx-auto px-4">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className={`text-4xl font-bold mb-12 ${styles.gradientText}`}>Tokenomics</h2>
                        <div className={styles.featureCard + " p-8 rounded-2xl"}>
                            <div className={`text-6xl font-bold ${styles.gradientText} mb-4`}>
                                {siteData.content.tokenomics.supply}
                            </div>
                            <p className="text-xl text-gray-300">Total Supply</p>
                        </div>
                    </div>
                </div>
            </section >

            <footer className="py-12 border-t border-white/10">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col gap-8">
                        {/* Logo and Social Links */}
                        <div className="flex flex-col md:flex-row items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className={styles.gradientBorder + " p-[2px] rounded-full"}>
                                    <img
                                        src={siteData.media.logo}
                                        alt="Logo"
                                        className="w-8 h-8 rounded-full object-cover"
                                    />
                                </div>
                                <span className={`text-lg font-bold ${styles.gradientText}`}>
                                    {siteData.token_info.name}
                                </span>
                            </div>
                            <div className="flex items-center gap-6">
                                {/* Twitter (X) Link with Icon */}
                                <a
                                    href="https://x.com/ai_kyora"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-400 hover:text-primary transition-colors flex items-center gap-2"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        className="text-gray-400 hover:text-blue-400 transition-colors"
                                    >
                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                    </svg>
                                    <span>Twitter</span>
                                </a>

                                {/* Discord Link with Icon */}
                                <a
                                    href="https://discord.gg/KdT6mkmt"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-400 hover:text-primary transition-colors flex items-center gap-2"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        className="text-gray-400 hover:text-indigo-500 transition-colors"
                                    >
                                        <path d="M14.82 4.26a10.14 10.14 0 0 0-.53 1.1 14.66 14.66 0 0 0-4.58 0 10.14 10.14 0 0 0-.53-1.1 16 16 0 0 0-4.13 1.3 17.33 17.33 0 0 0-3 11.59 16.6 16.6 0 0 0 5.07 2.59A12.89 12.89 0 0 0 8.23 18a9.65 9.65 0 0 1-1.71-.83 3.39 3.39 0 0 0 .42-.33 11.66 11.66 0 0 0 10.12 0q.21.18.42.33a10.84 10.84 0 0 1-1.71.84 12.41 12.41 0 0 0 1.08 1.78 16.44 16.44 0 0 0 5.06-2.59 17.22 17.22 0 0 0-3-11.59 16.09 16.09 0 0 0-4.09-1.35zM8.68 14.81a1.94 1.94 0 0 1-1.8-2 1.93 1.93 0 0 1 1.8-2 1.93 1.93 0 0 1 1.8 2 1.93 1.93 0 0 1-1.8 2zm6.64 0a1.94 1.94 0 0 1-1.8-2 1.93 1.93 0 0 1 1.8-2 1.92 1.92 0 0 1 1.8 2 1.92 1.92 0 0 1-1.8 2z" />
                                    </svg>
                                    <span>Discord</span>
                                </a>
                            </div>
                        </div>

                        {/* Footer Credit */}
                        <div className="flex items-center justify-center pt-4 border-t border-white/5">
                            <p className="text-sm text-gray-500">
                                Autonomously crafted by{' '}
                                <a
                                    href="https://kyora.ai"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 group"
                                >
                                    <span className="font-semibold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-blue-300 to-blue-400 group-hover:opacity-90 transition-opacity">
                                        Kyora AI
                                    </span>
                                    <div className="relative">
                                        <div className="absolute -inset-[1px] bg-gradient-to-r from-blue-400/20 to-transparent rounded-full blur-[1px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                        <div className="relative w-8 h-8 rounded-full overflow-hidden border border-blue-400/30">
                                            <img
                                                src="/kyora.png"
                                                alt="Kyora AI"
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    </div>
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </footer>
        </div >
    );
};

export default TokenSite;